// skeleton/app/src/app/shared/services/cognito.service.ts

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Constants } from '../constants';
import { Amplify } from 'aws-amplify';
import {
  signUp,
  signIn,
  signOut,
  fetchAuthSession,
  getCurrentUser,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { StorageService } from './token-storage.service';

export interface ICognitoUser {
  email: string;
  password: string;
}

export interface ICognitoUsermapping {
  groupId: number;
  roleId: number;
  email: string;
  orgid: number;
}

@Injectable({
  providedIn: 'root',
})
export class CognitoService {
  private authenticationSubject: BehaviorSubject<any>;

  constant: Constants = new Constants();

  private checkInterval: ReturnType<typeof setInterval> | null = null;

  private sessionDuration = 60 * 60 * 1000;

  constructor(
    private storage: StorageService
  ) {
    try {
      const sessionStorageWrapper = {
        setItem: async (
          key: string,
          value: string
        ): Promise<void> => {
          window.sessionStorage.setItem(
            key,
            value
          );
        },

        getItem: async (
          key: string
        ): Promise<string | null> => {
          return window.sessionStorage.getItem(
            key
          );
        },

        removeItem: async (
          key: string
        ): Promise<void> => {
          window.sessionStorage.removeItem(
            key
          );
        },

        clear: async (): Promise<void> => {
          window.sessionStorage.clear();
        },
      };

      Amplify.configure({
        Auth: {
          Cognito: {
            userPoolId:
              environment.cognito.userPoolId,

            userPoolClientId:
              environment.cognito
                .userPoolWebClientId,
          },
        },
      });

      cognitoUserPoolsTokenProvider.setKeyValueStorage(
        sessionStorageWrapper
      );
    } catch (error) {
      this.logError(
        'Cognito initialization failed',
        error
      );
    }

    this.authenticationSubject =
      new BehaviorSubject<boolean>(
        false
      );
  }

  public signUp(
    user: ICognitoUser
  ): Promise<any> {
    try {
      return signUp({
        username: user.email,
        password: user.password,
      });
    } catch (error) {
      this.logError(
        'Cognito sign up failed',
        error
      );

      return Promise.reject(error);
    }
  }

  public signIn(
    user: ICognitoUser
  ): Promise<any> {
    try {
      return this.isAuthenticated()
        .then(
          (
            isLoggedIn
          ) => {
            if (
              isLoggedIn
            ) {
              return signOut();
            }

            return undefined;
          }
        )
        .then(() => {
          return signIn({
            username:
              user.email,

            password:
              user.password,
          });
        })
        .then(async () => {
          try {
            const session =
              await fetchAuthSession();

            const idToken =
              session.tokens
                ?.idToken;

            const accessToken =
              session.tokens
                ?.accessToken;

            if (idToken) {
              this.storage.set(
                'cognitoidToken',
                idToken.toString()
              );
            }

            if (
              accessToken
            ) {
              this.storage.set(
                'cognitoaccessToken',
                accessToken.toString()
              );
            }

            this.startTokenRefreshInterval();

            this.authenticationSubject.next(
              true
            );
          } catch (error) {
            this.logError(
              'Cognito session setup failed',
              error
            );

            throw error;
          }
        });
    } catch (error) {
      this.logError(
        'Cognito sign in failed',
        error
      );

      return Promise.reject(error);
    }
  }

  public startTokenRefreshInterval(): void {
    try {
      if (
        this.checkInterval
      ) {
        clearInterval(
          this.checkInterval
        );

        this.checkInterval =
          null;
      }

      this.checkInterval =
        setInterval(
          async () => {
            try {
              const idToken =
                this.getCognitoIdToken();

              if (!idToken) {
                return;
              }

              const payload =
                this.getTokenPayload(
                  idToken
                );

              if (
                !payload ||
                typeof payload.exp !==
                  'number'
              ) {
                return;
              }

              const expiry =
                payload.exp *
                1000;

              const currentTime =
                Date.now();

              const timeLeft =
                expiry -
                currentTime;

              if (
                timeLeft <
                  180000 &&
                timeLeft > 0
              ) {
                const result =
                  await this.refreshTokensAfterExpiry();

                if (
                  !result
                ) {
                  this.clearCognitoState();

                  this.authenticationSubject.next(
                    false
                  );
                }
              }
            } catch (error) {
              this.logError(
                'Cognito token refresh check failed',
                error
              );
            }
          },
          60000
        );
    } catch (error) {
      this.logError(
        'Cognito token refresh initialization failed',
        error
      );
    }
  }

  public signOut(): Promise<any> {
    try {
      return signOut()
        .then(() => {
          this.clearCognitoState();

          if (
            this.checkInterval
          ) {
            clearInterval(
              this.checkInterval
            );

            this.checkInterval =
              null;
          }

          this.authenticationSubject.next(
            false
          );
        })
        .catch(
          (error) => {
            this.logError(
              'Cognito sign out failed',
              error
            );

            throw error;
          }
        );
    } catch (error) {
      this.logError(
        'Cognito sign out setup failed',
        error
      );

      return Promise.reject(error);
    }
  }

  public async getUser(): Promise<any> {
    try {
      const {
        username,
        userId: id,
      } =
        await getCurrentUser();

      const attributes =
        await fetchUserAttributes();

      return {
        id,
        username,
        attributes,
      };
    } catch (error) {
      this.logError(
        'Cognito user retrieval failed',
        error
      );

      throw error;
    }
  }

  async getToken(): Promise<any> {
    try {
      const session =
        await fetchAuthSession();

      if (
        session &&
        session.tokens &&
        session.tokens.idToken
      ) {
        return session.tokens
          .idToken;
      }

      return null;
    } catch (error) {
      this.logError(
        'Cognito token retrieval failed',
        error
      );

      throw error;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      const user =
        await this.getUser();

      return !!user;
    } catch {
      return false;
    }
  }

  public getCognitoIdToken():
    string | null {
    try {
      return this.storage.get(
        'cognitoidToken'
      );
    } catch (error) {
      this.logError(
        'Cognito ID token retrieval failed',
        error
      );

      return null;
    }
  }

  public getCognitoAccessToken():
    string | null {
    try {
      return this.storage.get(
        'cognitoaccessToken'
      );
    } catch (error) {
      this.logError(
        'Cognito access token retrieval failed',
        error
      );

      return null;
    }
  }

  public getCognitoRefreshToken():
    string | null {
    try {
      return this.storage.get(
        'cognitorefreshToken'
      );
    } catch (error) {
      this.logError(
        'Cognito refresh token retrieval failed',
        error
      );

      return null;
    }
  }

  isTokenValid(): boolean {
    try {
      const accessToken =
        this.getCognitoAccessToken();

      if (!accessToken) {
        return false;
      }

      const payload =
        this.getTokenPayload(
          accessToken
        );

      if (
        !payload ||
        typeof payload.exp !==
          'number'
      ) {
        return false;
      }

      const expiryDate =
        new Date(
          1000 *
            payload.exp
        );

      const currentDate =
        new Date();

      if (
        expiryDate >
        currentDate
      ) {
        return true;
      }

      this.refreshTokensAfterExpiry()
        .then(
          (result) => {
            if (
              !result
            ) {
              this.clearCognitoState();

              this.authenticationSubject.next(
                false
              );
            }
          }
        )
        .catch(
          (error) => {
            this.logError(
              'Cognito token refresh failed',
              error
            );

            this.clearCognitoState();

            this.authenticationSubject.next(
              false
            );
          }
        );

      return false;
    } catch (error) {
      this.logError(
        'Cognito token validation failed',
        error
      );

      return false;
    }
  }

  public async refreshTokensAfterExpiry():
    Promise<{
      idToken: string;
      accessToken: string;
    } | null> {
    try {
      const session =
        await fetchAuthSession({
          forceRefresh: true,
        });

      const idToken =
        session.tokens
          ?.idToken;

      const accessToken =
        session.tokens
          ?.accessToken;

      if (
        idToken &&
        accessToken
      ) {
        const idTokenValue =
          idToken.toString();

        const accessTokenValue =
          accessToken.toString();

        this.storage.set(
          'cognitoidToken',
          idTokenValue
        );

        this.storage.set(
          'cognitoaccessToken',
          accessTokenValue
        );

        return {
          idToken:
            idTokenValue,

          accessToken:
            accessTokenValue,
        };
      }

      return null;
    } catch (error) {
      this.logError(
        'Cognito token refresh failed',
        error
      );

      return null;
    }
  }

  private clearCognitoState(): void {
    try {
      this.storage.remove(
        'cognitoidToken'
      );
    } catch (error) {
      this.logError(
        'Cognito ID token cleanup failed',
        error
      );
    }

    try {
      this.storage.remove(
        'cognitoaccessToken'
      );
    } catch (error) {
      this.logError(
        'Cognito access token cleanup failed',
        error
      );
    }

    try {
      this.storage.remove(
        'cognitorefreshToken'
      );
    } catch (error) {
      this.logError(
        'Cognito refresh token cleanup failed',
        error
      );
    }
  }

  private getTokenPayload(
    token: string
  ): any | null {
    try {
      const parts =
        token.split('.');

      if (
        parts.length < 2
      ) {
        return null;
      }

      const normalized =
        parts[1]
          .replace(
            /-/g,
            '+'
          )
          .replace(
            /_/g,
            '/'
          );

      const padded =
        normalized.padEnd(
          Math.ceil(
            normalized.length /
              4
          ) * 4,
          '='
        );

      const decoded =
        atob(padded);

      return JSON.parse(
        decoded
      );
    } catch (error) {
      this.logError(
        'Cognito token parsing failed',
        error
      );

      return null;
    }
  }

  private logError(
    context: string,
    error: unknown
  ): void {
    try {
      const messages:
        string[] = [];

      let currentError:
        unknown = error;

      let depth = 0;

      while (
        currentError &&
        depth < 5
      ) {
        if (
          currentError instanceof
          Error
        ) {
          messages.push(
            currentError.message ||
              currentError.name
          );

          const nestedError =
            currentError as Error & {
              cause?: unknown;
              innerException?: unknown;
              innerError?: unknown;
            };

          currentError =
            nestedError.cause ??
            nestedError.innerException ??
            nestedError.innerError ??
            null;
        } else if (
          typeof currentError ===
            'object' &&
          currentError !==
            null &&
          'message' in
            currentError &&
          typeof (
            currentError as {
              message?: unknown;
            }
          ).message ===
            'string'
        ) {
          messages.push(
            (
              currentError as {
                message: string;
              }
            ).message
          );

          currentError =
            null;
        } else {
          messages.push(
            'Unknown error'
          );

          currentError =
            null;
        }

        depth++;
      }

      console.error(
        `${context}${
          messages.length
            ? `: ${messages.join(
                ' | '
              )}`
            : ''
        }`
      );
    } catch {
      console.error(
        context
      );
    }
  }
}